import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Star, Camera } from "lucide-react";
import { api, apiErr } from "@/lib/api";

const BRAND = "#d81f26";

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} data-testid={`${testid}-${n}`} onClick={() => onChange(n)} type="button">
          <Star size={26} className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewModal({ order, onClose, onDone }) {
  const [ratings, setRatings] = useState(() => Object.fromEntries((order.items || []).map((it) => [it.product_id, 0])));
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const r = new FileReader();
      r.onload = () => setPhotos((p) => (p.length >= 4 ? p : [...p, r.result]));
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async () => {
    const entries = Object.entries(ratings).filter(([, r]) => r > 0);
    if (entries.length === 0) { toast.error("Kam se kam ek product ko rate karein"); return; }
    setBusy(true);
    try {
      for (const [pid, rating] of entries) {
        await api.post(`/products/${pid}/reviews`, { rating, comment: comment.trim(), photos });
      }
      toast.success("Review ke liye dhanyavaad!");
      onDone?.();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="review-modal" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto w-11 h-1.5 rounded-full bg-slate-200 mb-4 sm:hidden" />
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-extrabold">Rate &amp; Review</h2>
            <p className="text-xs text-slate-500 mt-0.5">Order #{order.order_no}</p>
          </div>
          <button data-testid="review-close" onClick={onClose} className="p-1 -mr-1 text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>

        <p className="text-sm font-bold text-slate-600 mt-5 mb-1">Products</p>
        <div className="divide-y divide-slate-100">
          {(order.items || []).map((it) => (
            <div key={it.product_id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex items-center gap-2 min-w-0">
                {it.image ? <img src={it.image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" /> : <div className="w-9 h-9 rounded-lg bg-slate-100 shrink-0" />}
                <span className="text-sm font-semibold truncate">{it.name}</span>
              </div>
              <Stars value={ratings[it.product_id] || 0} onChange={(v) => setRatings((s) => ({ ...s, [it.product_id]: v }))} testid={`prod-star-${it.product_id}`} />
            </div>
          ))}
        </div>

        <p className="text-sm font-bold text-slate-600 mt-4 mb-2">Comment (optional)</p>
        <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Apna experience likhein…" className="w-full h-20 rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:ring-2" style={{ "--tw-ring-color": BRAND }} />

        <p className="text-sm font-bold text-slate-600 mt-4 mb-2">Photos (optional)</p>
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <div key={p} className="relative">
              <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <button data-testid={`review-photo-remove-${i}`} onClick={() => setPhotos((a) => a.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center"><X size={12} /></button>
            </div>
          ))}
          {photos.length < 4 && (
            <button data-testid="review-add-photo" onClick={() => fileRef.current?.click()} className="w-16 h-16 rounded-lg border-[1.5px] border-dashed flex flex-col items-center justify-center gap-0.5" style={{ borderColor: BRAND, color: BRAND }}>
              <Camera size={20} /><span className="text-[10px] font-semibold">Add</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickPhotos} />
        </div>

        <div className="flex gap-3 mt-6">
          <button data-testid="review-cancel" onClick={onClose} className="flex-1 h-12 rounded-xl border border-slate-200 font-bold text-slate-700">Cancel</button>
          <button data-testid="review-submit" onClick={submit} disabled={busy} className="flex-1 h-12 rounded-xl text-white font-bold disabled:opacity-60" style={{ backgroundColor: BRAND }}>{busy ? "…" : "Submit Review"}</button>
        </div>
      </div>
    </div>
  );
}

